<template>
	<div v-if="isOpen" class="modal-overlay" @click="closeModal">
		<div class="modal-content" @click.stop>
			<div class="modal-header">
				<h3>{{ $t("adminPanelPage.userPermissionsModal.title") }}: {{ user.name || user.email }}</h3>
				<button @click="closeModal" class="close-btn">&times;</button>
			</div>
			<div class="modal-body">
				<div v-if="user" class="user-info">
					<div class="user-roles">
						<strong>{{ $t("adminPanelPage.userPermissionsModal.roles") }}: </strong>
						<span v-if="user.roles && user.roles.length > 0">
							{{ user.roles.map((role: any) => role.name).join(", ") }}
						</span>
						<span v-else class="no-roles">{{ $t("adminPanelPage.userPermissionsModal.noRoles") }}</span>
					</div>
				</div>

				<div class="permissions-section">
					<h4>{{ $t("adminPanelPage.userPermissionsModal.permissions") }}({{ permissions.length }})</h4>
					<div v-if="loading" class="loading-permissions">
						{{ $t("adminPanelPage.userPermissionsModal.loadingPermissions") }}
					</div>
					<div v-else-if="permissions.length > 0" class="permissions-list">
						<div v-for="permission in permissions" :key="permission.id" class="permission-item">
							<span class="permission-name">{{ permission.name }}: </span>
							<span class="permission-description">{{ permission.description }}</span>
						</div>
					</div>
					<div v-else class="no-permissions">
						{{ $t("adminPanelPage.userPermissionsModal.noPermissions") }}
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
interface Props {
	isOpen: boolean
	user: any | null
	permissions: any[]
	loading?: boolean
}

interface Emits {
	(e: "close"): void
}

defineProps<Props>()
const emit = defineEmits<Emits>()

const closeModal = () => {
	emit("close")
}
</script>

<style scoped>
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
}

.modal-content {
	background: white;
	border-radius: 8px;
	width: 90%;
	max-width: 600px;
	max-height: 80vh;
	overflow-y: auto;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 20px;
	border-bottom: 1px solid #e0e0e0;
	background-color: #f8f9fa;
}

.modal-header h3 {
	margin: 0;
	color: #333;
}

.close-btn {
	background: none;
	border: none;
	font-size: 24px;
	cursor: pointer;
	color: #666;
	padding: 0;
	width: 30px;
	height: 30px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.close-btn:hover {
	color: #333;
}

.modal-body {
	padding: 20px;
}

.user-info {
	margin-bottom: 20px;
	padding-bottom: 20px;
	border-bottom: 1px solid #e0e0e0;
}

.user-info h4 {
	margin: 0 0 8px 0;
	color: #333;
}

.user-email {
	margin: 0 0 12px 0;
	color: #666;
	font-size: 14px;
}

.user-roles {
	font-size: 14px;
}

.user-roles strong {
	color: #333;
}

.no-roles {
	color: #999;
	font-style: italic;
}

.permissions-section h4 {
	margin: 0 0 16px 0;
	color: #333;
}

.loading-permissions {
	text-align: center;
	color: #666;
	padding: 20px;
	font-style: italic;
}

.permissions-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.permission-item {
	padding: 8px 12px;
	border: 1px solid #e0e0e0;
	border-radius: 4px;
	background-color: #fff;
}

.permission-name {
	font-weight: 500;
	color: #333;
	margin-bottom: 2px;
}

.permission-description {
	font-size: 13px;
	color: #666;
}

.no-permissions {
	text-align: center;
	color: #999;
	font-style: italic;
	padding: 20px;
	background-color: #f8f9fa;
	border-radius: 6px;
}

@media (max-width: 768px) {
	.modal-content {
		width: 95%;
		margin: 20px;
	}

	.modal-body {
		padding: 16px;
	}
}
</style>
